import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import { useCommoditySettings } from '../../../../hooks/useCommoditySettings';
import { MeasurementSystem } from '../../../../providers/User/User.model';
import { CommodityList } from '../../../../redux/api/commodities/commodities.model';
import { updateNewTripCommodity } from '../../../../redux/features/trip/tripSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { formatDimensions } from '../../../../utils/commodity/commodityUtils';
import TwField, { TwInputTypes } from '../../../TwFields/TwFields';
import { styled } from '@mui/material/styles';

interface TwTripCommodityFromOrderRowProps {
  commodity: CommodityList;
  measurementSystem: MeasurementSystem;
}

// Style constants
const COLOR_LABEL = '#6B7280';
const COLOR_UNIT = '#9CA3AF';
const COLOR_DIVIDER = '#E5E7EB';
const FONT_SIZE_LABEL = '14px';
const FONT_SIZE_TITLE = '16px';
const FONT_WEIGHT_LABEL = 500;
const FONT_WEIGHT_TITLE = 700;
const MARGIN_LABEL = '-5px 10px 5px 5px';

// Styled components
const StyledInfoLabel = styled(Typography)(() => ({
  color: COLOR_LABEL,
  fontWeight: FONT_WEIGHT_LABEL,
  fontSize: FONT_SIZE_LABEL,
  lineHeight: 1.5,
  margin: MARGIN_LABEL,
  alignSelf: 'center',
}));
const StyledInfoValue = styled(Typography)(() => ({
  fontWeight: 400,
  fontSize: '1rem',
  lineHeight: 1.5,
}));
const StyledTitle = styled(Typography)(() => ({
  fontWeight: FONT_WEIGHT_TITLE,
  fontSize: FONT_SIZE_TITLE,
}));
const StyledDivider = styled(Divider)(() => ({
  width: '98%',
  borderColor: COLOR_DIVIDER,
  margin: '5px 16px auto',
}));

const TwTripCommodityFromOrderRow: React.FC<TwTripCommodityFromOrderRowProps> = ({
  commodity,
  measurementSystem,
}) => {
  const { new_trip_commodities = [] } = useAppSelector((state) => state.trip);
  const commoditiesToUse = new_trip_commodities;
  const dispatch = useAppDispatch();

  const { isMakeEnabled, isModelEnabled, isCommodityIdEnabled } = useCommoditySettings();

  // Local state for all editable fields
  const [localQuantity, setLocalQuantity] = useState(commodity.quantity?.toString() ?? '');
  const [localWeight, setLocalWeight] = useState(commodity.weight ?? '');
  const [localNotes, setLocalNotes] = useState(commodity.notes ?? '');

  // Functions
  const handleRemoveCommodity = (commodityId: string): void => {
    const updatedCommodities = commoditiesToUse.filter((c) => c.ui_uid !== commodityId);
    dispatch(updateNewTripCommodity(updatedCommodities));
  };

  // Effects
  useEffect(() => {
    setLocalQuantity(commodity.quantity?.toString() ?? '');
    setLocalWeight(commodity.weight ?? '');
    setLocalNotes(commodity.notes ?? '');
  }, [commodity]);

  // Handlers for blur events to update Redux only if value changed
  const handleQuantityBlur = (): void => {
    const parsedQuantity = localQuantity === '' ? null : Number(localQuantity);
    if (parsedQuantity !== (commodity.quantity ?? null)) {
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === commodity.ui_uid ? { ...c, quantity: parsedQuantity } : c
      );
      dispatch(updateNewTripCommodity(updatedCommodities));
    }
  };

  const handleWeightBlur = (): void => {
    const parsedWeight = localWeight === '' ? null : Number(localWeight);
    if (parsedWeight !== (commodity.weight ?? null)) {
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === commodity.ui_uid ? { ...c, weight: parsedWeight } : c
      );
      dispatch(updateNewTripCommodity(updatedCommodities));
    }
  };

  const handleNotesBlur = (): void => {
    if (localNotes !== (commodity.notes ?? '')) {
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === commodity.ui_uid ? { ...c, notes: localNotes } : c
      );
      dispatch(updateNewTripCommodity(updatedCommodities));
    }
  };

  // Render component when order_id is present
  if (commodity?.order_id) {
    return (
      <Box sx={{ p: 2, background: '#fff', borderRadius: 2, mb: 1 }}>
        {/* Header: Title and controls */}
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={7} sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Commodity Selector */}
            <StyledTitle variant="h6">
              {commodity.name || ''} {isCommodityIdEnabled && commodity.readable_commodity_id && `(ID ${commodity.readable_commodity_id})`}
            </StyledTitle>
          </Grid>
          <Grid item xs={2}>
            <Box display="flex" alignItems="center" height="100%">
              <StyledInfoLabel variant="body2">Qty</StyledInfoLabel>
              <TwField
                id="quantity"
                name="quantity"
                value={localQuantity}
                onInputChange={(e): void => setLocalQuantity(e.target.value)}
                onInputBlur={handleQuantityBlur}
                inputType={TwInputTypes.number}
                sx={{
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                    fontSize: '1rem',
                    lineHeight: 1.5
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={2.5}>
            {/* Weight */}
            <Box display="flex" alignItems="center" height="100%">
              <StyledInfoLabel variant="body2">Weight</StyledInfoLabel>
              <TwField
                sx={{
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                    lineHeight: 1.5
                  }
                }}
                id="weight"
                name="weight"
                value={localWeight}
                onInputChange={(e): void => setLocalWeight(e.target.value)}
                onInputBlur={handleWeightBlur}
                inputType={TwInputTypes.number}
                endAdorment={
                  MeasurementSystem.IMPERIAL === measurementSystem ? (
                    <Typography color={COLOR_UNIT} sx={{ fontSize: '14px' }}>
                      lb
                    </Typography>
                  ) : (
                    <Typography color={COLOR_UNIT} sx={{ fontSize: '14px' }}>
                      kg
                    </Typography>
                  )
                }
              />
            </Box>
          </Grid>
          <Grid item xs={0.5} sx={{ paddingLeft: '10px !important' }}>
            {/* Close Icon */}
            <IconButton
              onClick={(): void => handleRemoveCommodity(commodity.ui_uid)}
              sx={{ mt: '-5px' }}
              size="small"
              aria-label="Remove commodity"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Grid>
          <StyledDivider />
        </Grid>

        {/* Info section */}
        <Grid container spacing={2} sx={{ mb: '15px', mt: '1px' }}>
          <Grid item xs={3}>
            <StyledInfoLabel variant="body2">Customer</StyledInfoLabel>
            <StyledInfoValue variant="body1">{commodity.customer || ''}</StyledInfoValue>
          </Grid>
          <Grid item xs={2}>
            <StyledInfoLabel variant="body2">Cust. #</StyledInfoLabel>
            <StyledInfoValue variant="body1">{commodity.customer_number || '-'}</StyledInfoValue>
          </Grid>
          <Grid item xs={2}>
            <StyledInfoLabel variant="body2">Order #</StyledInfoLabel>
            <StyledInfoValue variant="body1" color="primary" sx={{ cursor: 'pointer' }}>
              {commodity.order_readable_id || '-'}
            </StyledInfoValue>
          </Grid>
          <Grid item xs={3.5}>
            <StyledInfoLabel variant="body2">L x W x H</StyledInfoLabel>
            <StyledInfoValue variant="body1">
              {formatDimensions(
                commodity.length,
                commodity.width,
                commodity.height,
                measurementSystem
              ) || '-'}
            </StyledInfoValue>
          </Grid>
          <Grid item xs={1.5}>
            <StyledInfoLabel variant="body2">Permit</StyledInfoLabel>
            <StyledInfoValue variant="body1">
              {commodity.permit ? 'Required' : 'Not Required'}
            </StyledInfoValue>
          </Grid>
        </Grid>

        {/* Make and model */}
        {(isMakeEnabled || isModelEnabled) && (
          <Grid container spacing={2}>
            {isMakeEnabled && (
              <Grid item xs={3}>
                <StyledInfoLabel variant="body2">Make</StyledInfoLabel>
                <StyledInfoValue variant="body1">{commodity.make || '-'}</StyledInfoValue>
              </Grid>
            )}
            {isModelEnabled && (
              <Grid item xs={9}>
                <StyledInfoLabel variant="body2">Model</StyledInfoLabel>
                <StyledInfoValue variant="body1">{commodity.model || '-'}</StyledInfoValue>
              </Grid>
            )}
          </Grid>
        )}

        {/* Third line: Notes */}
        <Grid container spacing={1} sx={{ mt: '10px' }}>
          <Grid item xs={12}>
            {/* Input: Notes */}
            <TwField
              id="notes"
              name="notes"
              placeholder="Add note..."
              value={localNotes}
              onInputChange={(e): void => setLocalNotes(e.target.value)}
              onInputBlur={handleNotesBlur}
              inputType={TwInputTypes.text}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return null;
};

export default TwTripCommodityFromOrderRow; 